import * as React from 'react';
import numeral from 'numeral';
import {
  TableCell,
  TableValue,
  TableEmptyValue,
  YearCellColor,
  YearCellValue,
  TrendIcon,
} from './CompareYearTable.styles';
import { getFormattedCurrency, getFormattedRange } from 'utils/format';
import { ICellRendererParams, ICurrencyFormat, IRange } from 'utils/types';
import { CHART_COLOR_PALLETTE, DECIMALS_FORMATS } from 'utils/constants';

export const cellRendererFramework = (
  params: ICellRendererParams,
  options: {
    isCurrency: boolean;
    format: ICurrencyFormat;
  }
) => {
  const valueStr =
    options && options.isCurrency
      ? getFormattedCurrency({
          value: params.value,
          currencyFormat: options.format,
          isCurrencySign: true,
          isUnitSign: true,
          isIdealDenomination: true,
        })
      : numeral(Math.abs(params.value)).format('0.0');
  return (
    <TableCell>
      {params.value === null || params.value === undefined ? (
        <TableEmptyValue>-</TableEmptyValue>
      ) : (
        <React.Fragment>
          {params.rowIndex === 2 && params.value !== 0 ? (
            <TrendIcon iconName="trending_flat" isNegative={params.value < 0} />
          ) : null}
          <TableValue>
            {params.rowIndex === 2 && params.value !== 0
              ? numeral(Math.abs(params.value / 100)).format(
                  `${DECIMALS_FORMATS[options.format.decimal]}%`
                )
              : valueStr}
          </TableValue>
        </React.Fragment>
      )}
    </TableCell>
  );
};

export const evenValueCellRendererFramework = (params: ICellRendererParams) => (
  <TableCell>
    {params.value === null || params.value === undefined ? (
      <TableEmptyValue>-</TableEmptyValue>
    ) : (
      <React.Fragment>
        {params.rowIndex === 2 && params.value !== 0 ? (
          <TrendIcon iconName="trending_flat" isNegative={params.value < 0} />
        ) : null}
        <TableValue>
          {params.rowIndex === 2 && params.value !== 0
            ? numeral(Math.abs(params.value / 100)).format('0.0%')
            : params.value}
        </TableValue>
      </React.Fragment>
    )}
  </TableCell>
);

export const yearCellRendererFramework = (params: {
  rowIndex: number;
  value: IRange;
}) => {
  if (params.rowIndex === 2) {
    return (
      <TableCell>
        <YearCellValue>YOY Change</YearCellValue>
      </TableCell>
    );
  }
  return (
    <TableCell>
      <YearCellColor color={CHART_COLOR_PALLETTE[params.rowIndex]} />
      <YearCellValue>
        {getFormattedRange(params.value, params.rowIndex === 0)}
      </YearCellValue>
    </TableCell>
  );
};

export const getColumnDefs = (currencyFormat?: ICurrencyFormat) => {
  if (!currencyFormat) {
    return [];
  }
  const currencyColumnCellRenderer = (params: ICellRendererParams) =>
    cellRendererFramework(params, {
      isCurrency: true,
      format: currencyFormat,
    });
  const regularColumnCellRenderer = (params: ICellRendererParams) =>
    cellRendererFramework(params, {
      isCurrency: false,
      format: currencyFormat,
    });
  return [
    {
      headerName: 'YEAR',
      field: 'year',
      type: 'numericColumn',
      headerClass: 'compare-year-table--header-year',
      cellRendererFramework: yearCellRendererFramework,
      width: 320,
    },
    {
      headerName: `SPEND`,
      field: 'spend',
      cellRendererFramework: currencyColumnCellRenderer,
    },
    {
      headerName: `REVENUE`,
      field: 'revenue',
      cellRendererFramework: currencyColumnCellRenderer,
    },
    {
      headerName: '% OF REVENUE',
      field: 'percentageRevenue',
      cellRendererFramework: regularColumnCellRenderer,
    },
    {
      headerName: 'HEADCOUNT',
      field: 'headcount',
      cellRendererFramework: evenValueCellRendererFramework,
    },
    {
      headerName: 'VENDORS',
      field: 'vendors',
      cellRendererFramework: evenValueCellRendererFramework,
    },
    {
      headerName: 'GEOGRAPHIES',
      field: 'geographies',
      cellRendererFramework: evenValueCellRendererFramework,
    },
  ];
};
